import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import useAlertStore from "store/alert";
import { uploadImage, useImgUpload } from "./service";

export type ImageType = "existing" | "order" | "updated" | "deleted" | "new";

export interface RoomImage {
  expsOrdV: number;
  imgFileExtNm: string;
  imgTypCd: string;
  resImgFileNm: string;
  resImgNm: string;
  resImgPhUrl: string;
  rgSeq: number;
  rmNo: string;
  thumImgPhUrl: string;
  uyn: string;
}

export type initialImagesStateProps = {
  id: string;
  preview: string;
  file?: File;
  width?: number;
  height?: number;
  origin?: RoomImage | null;
  type: ImageType;
  rgSeq?: number;
  expsOrdV?: number;
};

interface ImagesContextType {
  images: initialImagesStateProps[];
  setImages: Dispatch<SetStateAction<initialImagesStateProps[]>>;
  dragIndex: number | null;
  setDragIndex: Dispatch<SetStateAction<number | null>>;
  onAdd: ({
    files,
    formTyp,
  }: {
    files: FileList;
    formTyp: "update" | "create";
  }) => void;
  onDelete: (index: number) => void;
  onReplace: (index: number, files: FileList) => void;
  onDragStart: (index: number) => void;
  onDrop: (index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  rmNo: string | null;
  setRmNo: Dispatch<SetStateAction<string | null>>;
}

const ImagesContext = createContext<ImagesContextType | undefined>(undefined);

interface ImagesProviderProps {
  children: ReactNode;
}

export const ImagesProvider = ({ children }: ImagesProviderProps) => {
  const maxSize = 1 * 1024 * 1024;
  const onSetAlert = useAlertStore((state) => state.onSetAlert);

  const [rmNo, setRmNo] = useState<string | null>(null);

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [images, setImages] = useState<initialImagesStateProps[]>([
    {
      id: "",
      rgSeq: -1,
      expsOrdV: -1,
      preview: "",
      origin: null,
      type: "existing",
    },
  ]);

  const { uploadRoomImage } = useImgUpload();

  // 이미지 추가

  type IAddProps = {
    files: FileList;
    formTyp: "update" | "create";
  };
  const onAdd = async ({ files, formTyp }: IAddProps) => {
    if (!files) return;
    if (images.length + files.length > 10) {
      alert("최대 10개까지 등록이 가능합니다");
      return;
    }

    for (const file of Array.from(files)) {
      if (file.size > maxSize) {
        alert("1MB 이하 이미지만 업로드 가능합니다.");
        return false;
      }
    }

    if (rmNo) {
      // 이미지 등록
      if (formTyp === "create") {
        const imgList = await uploadImage(files);
        const newList = imgList.map((i: RoomImage, index: number) => {
          return {
            id: (index + 1).toString(),
            rgSeq: index + 1,
            expsOrdV: index + 1,
            preview: URL.createObjectURL(files[index]),
            origin: i,
            type: "existing",
          };
        });
        setImages([...images, ...newList]);
      }

      // 이미지 변경
      if (formTyp === "update") {
        uploadRoomImage(rmNo, files);
      }
    }
  };

  // 이미지 삭제
  const onDelete = (index: number) => {
    onSetAlert({
      isOpen: true,
      inner: "이미지를 삭제하시겠습니까?",
      cnclBtn: true,
      chkBnt: true,
      onSetConfirm: () => {
        const copy = [...images];
        copy[index].type = "deleted";

        const img = copy.filter((i) => i.type !== "deleted");
        const deleted = copy.filter((i) => i.type === "deleted");
        setImages([...img, copy[index]]);
      },
    });
  };

  // 이미지 교체
  const onReplace = async (index: number, files: FileList) => {
    if (files[0].size > maxSize) {
      alert("1MB 이하 이미지만 업로드 가능합니다.");
      return false;
    }

    const uploaded = await uploadImage(files);
    const copy = [...images];
    copy[index] = {
      ...copy[index],
      //   preview: uploaded[0].resImgPhUrl,
      preview: URL.createObjectURL(files[0]),
      origin: {
        ...uploaded[0],
        expsOrdV: copy[index].expsOrdV,
        rgSeq: copy[index].rgSeq,
      },

      type: copy[index].type === "existing" ? "updated" : "existing",
    };

    setImages(copy);
  };

  // Drag 시작
  const onDragStart = (index: number) => {
    setDragIndex(index);
  };

  // Drop
  const onDrop = (index: number) => {
    if (dragIndex === null) return;

    const updated = [...images];
    const draggedItem = updated[dragIndex];

    updated.splice(dragIndex, 1);
    updated.splice(index, 0, draggedItem);

    const start = Math.min(dragIndex, index);
    const end = Math.max(dragIndex, index);

    const updatedWithType: Array<initialImagesStateProps> = updated.map(
      (item, idx) => {
        if (idx >= start && idx <= end) {
          return {
            ...item,
            expsOrdV: idx + 1,
            type:
              item.type === "order" || item.type === "existing"
                ? "order"
                : item.type,
          }; // 순서 변경된 아이템만 type 추가
        }

        return item; // 나머지는 type 그대로
      }
    );

    setImages(updatedWithType);
    setDragIndex(null);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <ImagesContext.Provider
      value={{
        images,
        setImages,
        dragIndex,
        setDragIndex,
        onAdd,
        onDelete,
        onReplace,
        onDragStart,
        onDrop,
        onDragOver,
        rmNo,
        setRmNo,
      }}
    >
      {children}
    </ImagesContext.Provider>
  );
};

export const useImagesContext = (): ImagesContextType => {
  const context = useContext(ImagesContext);

  if (!context) {
    throw new Error(
      "useImagesContext는 ImagesProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};
